import { useCallback, useMemo } from "react";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePersistentState } from "@/hooks/use-persistent-state";

// Smallest a column may be dragged to, in pixels.
const MIN_COL_WIDTH = 40;

// Read the minimum intrinsic width of a CSS grid track so we can keep a
// definite min-width on the whole grid (header + body stay aligned and a
// horizontal scrollbar appears instead of squashing columns). Fixed tracks
// ("90px") contribute their value; flexible tracks ("minmax(280px, 2.5fr)")
// contribute their floor.
function parseTrackMin(value: string): number {
  const mm = value.match(/minmax\(\s*([\d.]+)px/);
  if (mm) return Math.round(parseFloat(mm[1]));
  const px = value.match(/^\s*([\d.]+)px\s*$/);
  if (px) return Math.round(parseFloat(px[1]));
  return 100;
}

export interface ColumnLayout {
  /** Value for `grid-template-columns`. */
  template: string;
  /** Definite minimum width of the whole grid, in pixels. */
  minWidth: number;
}

export interface ResizableColumns {
  /** Whether the operator has resized at least one column. */
  isCustomized: boolean;
  /** Clear every override and fall back to the default widths. */
  reset: () => void;
  /** Begin a drag-resize for `id` from a pointer-down on the handle. */
  startResize: (id: string, e: React.PointerEvent) => void;
  /** Build the grid template + min-width for an ordered list of column ids. */
  getLayout: (ids: string[]) => ColumnLayout;
}

/**
 * Per-user, drag-to-resize column widths persisted in localStorage.
 *
 * `defaults` maps column id -> default CSS grid track. Overrides are stored as
 * pixel numbers and rendered as fixed tracks; everything else keeps its default
 * (flexible) track so the grid still fills the panel when nothing is resized.
 */
export function useResizableColumns(
  storageKey: string,
  defaults: Record<string, string>,
): ResizableColumns {
  const [widths, setWidths] = usePersistentState<Record<string, number>>(
    storageKey,
    {},
  );

  const getLayout = useCallback(
    (ids: string[]): ColumnLayout => {
      let minWidth = 0;
      const template = ids
        .map((id) => {
          const override = widths[id];
          if (override != null) {
            minWidth += override;
            return `${override}px`;
          }
          const def = defaults[id] ?? "100px";
          minWidth += parseTrackMin(def);
          return def;
        })
        .join(" ");
      return { template, minWidth };
    },
    [widths, defaults],
  );

  const startResize = useCallback(
    (id: string, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const cell = (e.currentTarget as HTMLElement).parentElement;
      const startW = cell
        ? cell.getBoundingClientRect().width
        : (widths[id] ?? 100);
      const startX = e.clientX;

      const onMove = (ev: PointerEvent) => {
        const next = Math.max(
          MIN_COL_WIDTH,
          Math.round(startW + (ev.clientX - startX)),
        );
        setWidths((prev) => ({ ...prev, [id]: next }));
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [widths, setWidths],
  );

  const reset = useCallback(() => setWidths({}), [setWidths]);
  const isCustomized = useMemo(() => Object.keys(widths).length > 0, [widths]);

  return { isCustomized, reset, startResize, getLayout };
}

/**
 * Drag target rendered at the right edge of a header cell. The parent header
 * cell must be `position: relative`.
 */
export function ColumnResizeHandle({
  columnId,
  onResize,
}: {
  columnId: string;
  onResize: (id: string, e: React.PointerEvent) => void;
}) {
  return (
    <span
      onPointerDown={(e) => onResize(columnId, e)}
      onClick={(e) => e.stopPropagation()}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      className="absolute top-0 right-0 z-20 h-full w-1.5 cursor-col-resize touch-none select-none bg-transparent hover:bg-primary/40 active:bg-primary/60"
    />
  );
}

/** Small "Reset widths" button, shown only when columns have been resized. */
export function ResetColumnWidthsButton({
  show,
  onReset,
}: {
  show: boolean;
  onReset: () => void;
}) {
  if (!show) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-6 text-[11px] px-2 text-muted-foreground hover:text-foreground"
      onClick={onReset}
      title="Reset all column widths to their defaults"
    >
      <RotateCcw className="h-3 w-3 mr-1" /> Reset widths
    </Button>
  );
}
